import { useCallback, useRef } from "react";
import { toast } from "react-toastify";

/**
 * Hook for handling optimistic deletes with rollback on failure.
 *
 * Usage:
 *   const { optimisticDelete } = useOptimisticDelete();
 *
 *   const handleDeleteFolder = (folder) => {
 *     optimisticDelete({
 *       item: folder,
 *       itemType: 'folder',
 *       removeFromState: () => setFolders(prev => prev.filter(f => f.folder_id !== folder.folder_id)),
 *       restoreToState: () => setFolders(prev => [...prev, folder]),
 *       deleteRequest: () => deleteFolder(folder.folder_id),
 *       successMessage: `Folder "${folder.name}" deleted`,
 *       errorMessage: `Failed to delete folder "${folder.name}"`,
 *     });
 *   };
 */
export const useOptimisticDelete = () => {
  const pendingDeletes = useRef(new Map());

  const optimisticDelete = useCallback(
    async ({
      item,
      itemType,
      itemId,
      removeFromState,
      restoreToState,
      deleteRequest,
      successMessage,
      errorMessage,
    }) => {
      const id = itemId || item?.folder_id || item?.scenario_id;

      // Prevent duplicate delete attempts
      if (pendingDeletes.current.has(id)) {
        return;
      }
      pendingDeletes.current.set(id, true);

      // Step 1: Optimistically remove from UI immediately
      removeFromState();

      // Step 2: Show success toast (optimistic)
      toast.success(successMessage, {
        autoClose: 3000,
      });

      try {
        // Step 3: Send delete request in background
        await deleteRequest();
        // Success - nothing more to do, UI already updated
      } catch (error) {
        console.error(`Failed to delete ${itemType}:`, error);

        // Step 4: Rollback - restore item to UI
        restoreToState();

        // Show error toast
        toast.error(errorMessage || `Failed to delete. Please try again.`, {
          autoClose: 5000,
        });
      } finally {
        pendingDeletes.current.delete(id);
      }
    },
    [],
  );

  return { optimisticDelete };
};

export default useOptimisticDelete;
